import React from 'react';

import Loader from '../loader';
import axios from '../axios';
import usePresignedImageUpload from '../../lib/use_presigned_image_upload';

const SectionImage = ({
  eventId,
  mainImageUrl,
  setMainImageUrl,
  subImages,
  setContents,
}) => {
  const [loadingMain, setLoadingMain] = React.useState(false);
  const [loadingSub1, setLoadingSub1] = React.useState(false);
  const [loadingSub2, setLoadingSub2] = React.useState(false);
  const [loadingSub3, setLoadingSub3] = React.useState(false);
  const [loadingSub4, setLoadingSub4] = React.useState(false);
  const [loadingBatch, setLoadingBatch] = React.useState(false);
  const { upload } = usePresignedImageUpload();
  const mainImageRef = React.useRef(null);
  const subImageRef1 = React.useRef(null);
  const subImageRef2 = React.useRef(null);
  const subImageRef3 = React.useRef(null);
  const subImageRef4 = React.useRef(null);
  const batchImageRef = React.useRef(null);

  const subImage1 = React.useMemo(() => {
    return subImages[0] || {};
  }, [subImages]); // ex: {id: 1, photo_url_medium: 'https://...'}
  const subImage2 = React.useMemo(() => {
    return subImages[1] || {};
  }, [subImages]);
  const subImage3 = React.useMemo(() => {
    return subImages[2] || {};
  }, [subImages]);
  const subImage4 = React.useMemo(() => {
    return subImages[3] || {};
  }, [subImages]);

  const createImage = React.useCallback(() => {
    if (!mainImageUrl) {
      mainImageRef.current.click();
    } else if (!subImage1.photo_url_medium) {
      subImageRef1.current.click();
    } else if (!subImage2.photo_url_medium) {
      subImageRef2.current.click();
    } else if (!subImage3.photo_url_medium) {
      subImageRef3.current.click();
    } else if (!subImage4.photo_url_medium) {
      subImageRef4.current.click();
    }
  }, [mainImageUrl, subImage1, subImage2, subImage3, subImage4]);

  const updateSubImage = React.useCallback((subImageIndex) => {
    if (subImageIndex === 1) {
      subImageRef1.current.click();
    } else if (subImageIndex === 2) {
      subImageRef2.current.click();
    } else if (subImageIndex === 3) {
      subImageRef3.current.click();
    } else if (subImageIndex === 4) {
      subImageRef4.current.click();
    }
  }, []);

  const deleteSubImage = React.useCallback(
    (subImageId) => {
      axios
        .post('/api/event_edit/delete_content', {
          id: subImageId,
          event_id: eventId,
        })
        .then(() => {
          setContents((prev) =>
            prev.filter((content) => content.id !== subImageId)
          );
        })
        .catch(() => {
          alert('画像の削除に失敗しました。');
        });
    },
    [subImage1, subImage2, subImage3, subImage4]
  );

  const onChangeMainImage = React.useCallback(
    async (e) => {
      setLoadingMain(true);
      const file = e.target.files[0];
      if (!file) {
        setLoadingMain(false);
        return;
      }

      try {
        // Presigned URLでクラウドに直接アップロード
        const cacheData = await upload(file);
        // アップロードしたファイルの情報をDBに反映
        const res = await axios.post('/api/event/confirm_main_image_upload', {
          event_id: eventId,
          cache_data: cacheData,
        });

        setMainImageUrl(res.data.main_image_url);
      } catch (error) {
        alert(
          error?.response?.data?.error || '画像のアップロードに失敗しました。'
        );
      } finally {
        setLoadingMain(false);
      }
    },
    [eventId, upload]
  );

  const onChangeSubImage = React.useCallback(
    async (e) => {
      const file = e.target.files[0];
      if (!file) {
        return;
      }

      const contentId = e.target.name;
      const subImageIndex = Number(e.target.id);
      const setLoading = {
        1: setLoadingSub1,
        2: setLoadingSub2,
        3: setLoadingSub3,
        4: setLoadingSub4,
      }[subImageIndex];
      if (setLoading) setLoading(true);

      try {
        // Presigned URLでクラウドに直接アップロード
        const cacheData = await upload(file);
        // アップロードしたファイルの情報をDBに反映
        const res = await axios.post('/api/event_edit/confirm_sub_image_upload', {
          event_id: eventId,
          cache_data: cacheData,
          id: contentId,
        });

        setContents((prev) => {
          if (contentId) {
            return prev.map((content) => {
              if (content.id === res.data.id) {
                return res.data;
              } else {
                return content;
              }
            });
          } else {
            return [...prev, res.data];
          }
        });
      } catch {
        alert('画像のアップロードに失敗しました。');
      } finally {
        if (setLoading) setLoading(false);
      }
    },
    [eventId, upload]
  );

  const onChangeBatchImages = React.useCallback(
    async (e) => {
      const files = Array.from(e.target.files);
      // file inputをリセット（同じファイルを再選択可能にする）
      e.target.value = '';

      if (!files.length) return;

      if (files.length > 5) {
        alert('画像は最大5枚までです。');
        return;
      }

      const subLoadingSetters = [setLoadingSub1, setLoadingSub2, setLoadingSub3, setLoadingSub4];
      // アップロード対象のスロットだけローディング表示
      const activeSetters = [];
      setLoadingBatch(true);
      setLoadingMain(true);
      activeSetters.push(setLoadingMain);
      for (let i = 0; i < Math.min(files.length - 1, 4); i++) {
        subLoadingSetters[i](true);
        activeSetters.push(subLoadingSetters[i]);
      }

      try {
        // 全ファイルを並列でクラウドにアップロード
        const cacheDataList = await Promise.all(
          files.map((file) => upload(file))
        );

        // 1枚目 → main_image
        const mainCacheData = cacheDataList[0];
        const mainRes = await axios.post('/api/event/confirm_main_image_upload', {
          event_id: eventId,
          cache_data: mainCacheData,
        });
        setMainImageUrl(mainRes.data.main_image_url);

        // 2枚目以降 → sub_images（既存スロットは上書き）
        const subFiles = cacheDataList.slice(1);
        const newContents = [];
        for (let i = 0; i < subFiles.length; i++) {
          const existingContent = subImages[i];
          const res = await axios.post('/api/event_edit/confirm_sub_image_upload', {
            event_id: eventId,
            cache_data: subFiles[i],
            id: existingContent?.id || '',
          });
          newContents.push(res.data);
        }

        // stateを更新: アップロード分を反映
        setContents((prev) => {
          const result = [...prev];
          for (const newContent of newContents) {
            const idx = result.findIndex((c) => c.id === newContent.id);
            if (idx >= 0) {
              result[idx] = newContent;
            } else {
              result.push(newContent);
            }
          }
          return result;
        });
      } catch (error) {
        alert(
          error?.response?.data?.error || '画像のアップロードに失敗しました。'
        );
      } finally {
        setLoadingBatch(false);
        activeSetters.forEach((setter) => setter(false));
      }
    },
    [eventId, upload, subImages]
  );

  return (
    <section id="image">
      <div className="container col-md-8 col-lg-6">
        <p className="text-fz-14px text-fw-bold text-black-50 mb-2 mt-3 d-inline-block">
          画像
        </p>
      </div>
      <div className="shadow-sm mb-0 bg-white">
        <div className="container col-md-8 col-lg-6">
          <div className="row flex-nowrap overflow-auto">
            <div
              className="col-3 col-md-2 col-lg-2 bg-gray m-2 p-0 w-100"
              style={{ height: 90 }}
            >
              {loadingMain ? (
                <Loader isActive={loadingMain} />
              ) : mainImageUrl ? (
                <a onClick={() => mainImageRef.current.click()}>
                  <img
                    src={mainImageUrl}
                    className="w-100 object-fit-cover adjust-box-inner"
                  />
                </a>
              ) : (
                <a onClick={createImage}>
                  <NonImage number={1} />
                </a>
              )}
            </div>
            <div
              className="col-3 col-md-2 col-lg-2 bg-gray m-2 p-0 w-100"
              style={{ height: 90 }}
            >
              {loadingSub1 ? (
                <Loader isActive={loadingSub1} />
              ) : subImage1.photo_url_medium ? (
                <SubImage
                  subImage={subImage1}
                  updateSubImage={() => updateSubImage(1)}
                  deleteSubImage={() => deleteSubImage(subImage1.id)}
                />
              ) : (
                <a onClick={createImage}>
                  <NonImage number={2} />
                </a>
              )}
            </div>
            <div
              className="col-3 col-md-2 col-lg-2 bg-gray m-2 p-0 w-100"
              style={{ height: 90 }}
            >
              {loadingSub2 ? (
                <Loader isActive={loadingSub2} />
              ) : subImage2.photo_url_medium ? (
                <SubImage
                  subImage={subImage2}
                  updateSubImage={() => updateSubImage(2)}
                  deleteSubImage={() => deleteSubImage(subImage2.id)}
                />
              ) : (
                <a onClick={createImage}>
                  <NonImage number={3} />
                </a>
              )}
            </div>
            <div
              className="col-3 col-md-2 col-lg-2 bg-gray m-2 p-0 w-100"
              style={{ height: 90 }}
            >
              {loadingSub3 ? (
                <Loader isActive={loadingSub3} />
              ) : subImage3.photo_url_medium ? (
                <SubImage
                  subImage={subImage3}
                  updateSubImage={() => updateSubImage(3)}
                  deleteSubImage={() => deleteSubImage(subImage3.id)}
                />
              ) : (
                <a onClick={createImage}>
                  <NonImage number={4} />
                </a>
              )}
            </div>
            <div
              className="col-3 col-md-2 col-lg-2 bg-gray m-2 p-0 w-100"
              style={{ height: 90 }}
            >
              {loadingSub4 ? (
                <Loader isActive={loadingSub4} />
              ) : subImage4.photo_url_medium ? (
                <SubImage
                  subImage={subImage4}
                  updateSubImage={() => updateSubImage(4)}
                  deleteSubImage={() => deleteSubImage(subImage4.id)}
                />
              ) : (
                <a onClick={createImage}>
                  <NonImage number={5} />
                </a>
              )}
            </div>
            <input
              type="file"
              accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
              ref={mainImageRef}
              onChange={onChangeMainImage}
              style={{ display: 'none' }}
            />
            <input
              id={1}
              name={subImage1.id}
              type="file"
              accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
              ref={subImageRef1}
              onChange={onChangeSubImage}
              style={{ display: 'none' }}
            />
            <input
              id={2}
              name={subImage2.id}
              type="file"
              accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
              ref={subImageRef2}
              onChange={onChangeSubImage}
              style={{ display: 'none' }}
            />
            <input
              id={3}
              name={subImage3.id}
              type="file"
              accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
              ref={subImageRef3}
              onChange={onChangeSubImage}
              style={{ display: 'none' }}
            />
            <input
              id={4}
              name={subImage4.id}
              type="file"
              accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
              ref={subImageRef4}
              onChange={onChangeSubImage}
              style={{ display: 'none' }}
            />
          </div>
        </div>
      </div>
      <div className="container col-md-8 col-lg-6">
        <div className="mt-2 mb-1">
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm w-100"
            onClick={() => batchImageRef.current.click()}
            disabled={loadingBatch}
          >
            {loadingBatch ? 'アップロード中...' : 'まとめてアップロード（最大5枚）'}
          </button>
          <input
            type="file"
            accept="image/png, image/jpeg, image/heic, image/heif, .heic, .heif"
            multiple
            ref={batchImageRef}
            onChange={onChangeBatchImages}
            style={{ display: 'none' }}
          />
        </div>
        <p className="text-gray text-fz-small mt-1 mb-0">
          1枚目のカバー画像は、横縦比4:3で作成するときれいに表示されます。<br />
          2枚目以降は、実際のイベントの様子を写した画像がおすすめです。<br />
          画像をアップロードしていない場合は、タイトル（15文字以上）入力後に自動生成されます。
        </p>
      </div>
    </section>
  );
};

const SubImage = ({ subImage, updateSubImage, deleteSubImage }) => (
  <div>
    <a onClick={updateSubImage}>
      <img
        className="w-100 object-fit-cover adjust-box-inner"
        type="image"
        id={subImage.id}
        src={subImage.photo_url_medium}
      />
    </a>
    <a
      className="position-absolute bg-gray"
      style={{
        top: 0,
        right: 0,
        width: 24,
        height: 24,
        borderRadius: 12,
      }}
      onClick={deleteSubImage}
    >
      <p className="text-center text-fw-bold">×</p>
    </a>
  </div>
);

const NonImage = ({ number }) => (
  <div>
    <div className="w-100 object-fit-cover adjust-box-inner d-flex justify-content-end">
      <p className="bg-secondary text-white position-absolute top-0 pl-2 pr-2">
        {number}
      </p>
    </div>
    <div className="conteiner p-4">
      <div className="conteiner adjust-box adjust-box-3x2">
        <img
          src="/assets2019/img/icon_camera.svg"
          className="rounded w-100 object-fit-cover adjust-box-inner"
        />
      </div>
    </div>
  </div>
);

export default SectionImage;
